<template>
  <div v-if="data" class="relative h-full pb-2 overflow-x-auto bg-white">
    <table class="w-full text-sm text-left text-gray-900 divide-y divide-gray-200">
      <tbody>
        <BaseDetailsTableRow
          :name="$t('baseObject.code')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.code"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="getInputErrors('code')" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.name')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseInput
            :mode="mode"
            v-model="data.name"
            color="base"
            flat
            :disabled="mode == 'view'"
            show-error-as-tooltip
            :error="getInputErrors('name')" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('price-catalog.catalogsList')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownCatalogs"
            :selected-item="selectedCatalog"
            flat
            :mode="mode"
            size="sm"
            @update:selected="handleCatalogSelection"
            name="catalog"
            show-error-as-tooltip
            clearable
            :error="getInputErrors('catalog')" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('document-type.company')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseSelectDropdown
            :items="dropdownCompanies"
            :selected-item="selectedCompany"
            @update:selected="handleCompanySelection"
            name="company"
            :mode="mode"
            clearable
            show-error-as-tooltip
            :error="getInputErrors('company')" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isActive')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus v-if="mode == 'view'" :item="data.isActive" />
          <BaseToggle v-else name="isActive" v-model="data.isActive" />
        </BaseDetailsTableRow>

        <BaseDetailsTableRow
          :name="$t('baseObject.isFromManager')"
          :mode="mode"
          :isHorizontalLayout="isHorizontalLayout">
          <BaseTableCellStatus
            :item="data.isFromManager ? data.isFromManager : 0"
            :textTrue="$t('common.actions.yes')"
            :textFalse="$t('common.actions.no')" />
        </BaseDetailsTableRow>
      </tbody>
    </table>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted, watch } from 'vue';

import BaseTableCellStatus from '../common/BaseTableCellStatus.vue';
import BaseInput from '../common/BaseInput.vue';
import BaseDetailsTableRow from '../common/BaseDetailsTableRow.vue';
import BaseToggle from '../common/BaseToggle.vue';

import BaseSelectDropdown from '../common/BaseSelectDropdown.vue';
import { getPricingPolicyCatalogsOrder } from '@/services/api/prices';

import type { PricingPolicyCatalogOrderHeader, PricingPolicyMatrix } from '@/types/PricingTypes';
import type { SelectDropdownItem } from '@/types/Base';

import { useLookupsStore } from '@/stores/lookups';
import { storeToRefs } from 'pinia';
import { useVuelidate } from '@vuelidate/core';
import { required, helpers } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';

const { t: $t } = useI18n({ useScope: 'global' });

const props = defineProps<{
  mode: 'view' | 'edit';
  isHorizontalLayout: boolean;
  matrix: PricingPolicyMatrix;
}>();

const data = defineModel<PricingPolicyMatrix>();

const catalogs = ref<PricingPolicyCatalogOrderHeader[]>([]);
const selectedCatalog = ref<SelectDropdownItem | null>(null);

function handleCatalogSelection(item: SelectDropdownItem) {
  selectedCatalog.value = item ? item : null;
  if (data.value) {
    data.value.pricingPolicyCatalogOrderHeaderId = item ? (item.value as string) : undefined;
  }
}

const dropdownCatalogs = computed(() => {
  return catalogs.value.map((x) => ({
    value: x.id,
    name: x.name,
  })) as SelectDropdownItem[];
});

const selectedCompany = ref<SelectDropdownItem | null>(null);

const { getCompaniesLookup } = useLookupsStore();

const { dropdownCompanies } = storeToRefs(useLookupsStore());

function handleCompanySelection(item: SelectDropdownItem) {
  selectedCompany.value = item ? item : null;
  if (data.value && data.value.pricingPolicy) {
    data.value.pricingPolicy.companyId = item ? (item.value as string) : '';
  }
}

onMounted(async () => {
  await getCompaniesLookup();
  if (data.value && data.value.pricingPolicy) {
    const company = dropdownCompanies.value.find(
      (item) => item.value === data.value?.pricingPolicy?.companyId
    );
    if (company) selectedCompany.value = company;
    else if (dropdownCompanies.value?.[0]) handleCompanySelection(dropdownCompanies.value?.[0]);
  }
  const res = await getPricingPolicyCatalogsOrder();
  if (res) {
    catalogs.value = res;
  }
  if (data.value) {
    const catalog = dropdownCatalogs.value.find(
      (item) => item.value === data.value?.pricingPolicyCatalogOrderHeaderId
    );
    if (catalog) selectedCatalog.value = catalog;
  }
});

const isModified = ref<boolean>(false);

watch(
  () => data.value,
  (newData) => {
    validationModel.value.code = newData?.code || '';
    validationModel.value.name = newData?.name || '';
    validationModel.value.company = newData?.pricingPolicy?.companyId || '';
    validationModel.value.catalog = newData?.pricingPolicyCatalogOrderHeaderId || '';
    const valueChanged = JSON.stringify(newData) !== JSON.stringify(props.matrix);
    isModified.value = valueChanged;
  },
  { deep: true }
);

const validationModel = ref({
  code: data.value?.code || '',
  name: data.value?.name || '',
  company: data.value?.pricingPolicy?.companyId || '',
  catalog: data.value?.pricingPolicyCatalogOrderHeaderId || '',
});

const rules = computed(() => ({
  code: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  name: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  company: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
  catalog: {
    required: helpers.withMessage(`${$t('messages.errors.required')}`, required),
  },
}));

const $v = useVuelidate(rules, validationModel, { $lazy: true });

function getInputErrors(type: 'code' | 'name' | 'company' | 'catalog') {
  if ($v.value[type].required && $v.value[type].required.$invalid)
    return $v.value[type].required.$message;
  else return '';
}
</script>
